import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import { BookOpen, ListChecks, Sparkles, Loader2, Trash2, Clock, Search, X, FileSearch, FileText, Brain } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { listGenerations, deleteGeneration, deleteGenerations } from "@/lib/history.functions";
import { SiteHeader } from "@/components/SiteHeader";

type AssignmentType = "essay" | "summary" | "key_concepts" | "review" | "tutor";

type Row = {
  id: string;
  created_at: string;
  title: string;
  assignment_type: AssignmentType;
  prompt: string | null;
};

const TYPE_META: Record<AssignmentType, { label: string; icon: typeof BookOpen }> = {
  essay: { label: "Essay", icon: BookOpen },
  summary: { label: "Summary", icon: ListChecks },
  key_concepts: { label: "Key Concepts", icon: Sparkles },
  review: { label: "Review", icon: FileSearch },
  tutor: { label: "Tutor", icon: Brain },
};

const FALLBACK_META = { label: "Outline", icon: FileText };

type Filter = "all" | AssignmentType;

export const Route = createFileRoute("/history/")({
  head: () => ({
    meta: [
      { title: "Your past outlines — SmartEssay" },
      { name: "description", content: "Browse, search, and re-open every framed outline, summary, and key-concept map you've generated with SmartEssay." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: HistoryPage,
});

function HistoryPage() {
  const navigate = useNavigate();
  const list = useServerFn(listGenerations);
  const del = useServerFn(deleteGeneration);
  const delMany = useServerFn(deleteGenerations);
  const [checking, setChecking] = useState(true);
  const [rows, setRows] = useState<Row[] | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) {
        navigate({ to: "/auth" });
        return;
      }
      setChecking(false);
      list()
        .then((r) => setRows(r as Row[]))
        .catch((e) => toast.error((e as Error).message || "Couldn't load history."));
    });
  }, [navigate, list]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter !== "all" && r.assignment_type !== filter) return false;
      if (!q) return true;
      return (
        r.title.toLowerCase().includes(q) ||
        (r.prompt?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [rows, query, filter]);

  const allVisibleSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
  const someSelected = selected.size > 0;

  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleAllVisible() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) filtered.forEach((r) => next.delete(r.id));
      else filtered.forEach((r) => next.add(r.id));
      return next;
    });
  }

  function clearSelection() {
    setSelected(new Set());
  }

  async function onDelete(id: string) {
    if (!confirm("Delete this outline? This can't be undone.")) return;
    setDeletingId(id);
    try {
      await del({ data: { id } });
      setRows((prev) => (prev ? prev.filter((r) => r.id !== id) : prev));
      setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
    } catch (e) {
      toast.error((e as Error).message || "Couldn't delete.");
    } finally {
      setDeletingId(null);
    }
  }

  async function onBulkDelete() {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!confirm(`Delete ${ids.length} outline${ids.length === 1 ? "" : "s"}? This can't be undone.`)) return;
    setBulkDeleting(true);
    try {
      await delMany({ data: { ids } });
      setRows((prev) => (prev ? prev.filter((r) => !selected.has(r.id)) : prev));
      clearSelection();
      toast.success(`Deleted ${ids.length} outline${ids.length === 1 ? "" : "s"}.`);
    } catch (e) {
      toast.error((e as Error).message || "Couldn't delete.");
    } finally {
      setBulkDeleting(false);
    }
  }

  if (checking) return <div className="grid min-h-screen place-items-center text-muted-foreground">Loading…</div>;

  const FILTERS: { id: Filter; label: string }[] = [
    { id: "all", label: "All" },
    { id: "essay", label: "Essays" },
    { id: "review", label: "Reviews" },
    { id: "tutor", label: "Tutor sessions" },
    { id: "summary", label: "Summaries" },
    { id: "key_concepts", label: "Key Concepts" },
  ];

  return (
    <div className="min-h-screen bg-hero-gradient">
      <SiteHeader />

      <main className="mx-auto max-w-3xl px-6 pb-24 pt-6">
        <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Past outlines</h1>
        <p className="mt-1 text-muted-foreground">Search, filter, and pick up tutor sessions where you left off.</p>

        {/* Search + filters */}
        <div className="mt-6 space-y-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by title or prompt…"
              className="pl-9 pr-9"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                  filter === f.id
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card text-muted-foreground hover:border-primary/50 hover:text-foreground"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Bulk action bar */}
        {rows && rows.length > 0 && (
          <div className="mt-5 flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-2.5">
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <Checkbox
                checked={allVisibleSelected}
                onCheckedChange={toggleAllVisible}
                aria-label="Select all visible"
              />
              <span className="text-muted-foreground">
                {someSelected ? `${selected.size} selected` : "Select all"}
              </span>
            </label>
            <div className="flex items-center gap-2">
              {someSelected && (
                <Button variant="ghost" size="sm" onClick={clearSelection} disabled={bulkDeleting}>
                  Clear
                </Button>
              )}
              <Button
                variant="destructive"
                size="sm"
                onClick={onBulkDelete}
                disabled={!someSelected || bulkDeleting}
              >
                {bulkDeleting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />}
                Delete selected
              </Button>
            </div>
          </div>
        )}

        <div className="mt-4 space-y-3">
          {rows === null ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading your outlines…
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
              <p className="text-muted-foreground">No outlines yet.</p>
              <Button asChild className="mt-4">
                <Link to="/generator">Create your first outline</Link>
              </Button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center text-muted-foreground">
              No outlines match your search.
            </div>
          ) : (
            filtered.map((r) => {
              const meta = TYPE_META[r.assignment_type] ?? FALLBACK_META;
              const Icon = meta.icon;
              const isSelected = selected.has(r.id);
              return (
                <div
                  key={r.id}
                  className={`group flex items-start gap-3 rounded-xl border bg-card p-5 shadow-soft transition ${
                    isSelected ? "border-primary" : "border-border hover:border-primary/50 hover:shadow-glow"
                  }`}
                >
                  <div className="pt-1">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleOne(r.id)}
                      aria-label={`Select ${r.title}`}
                    />
                  </div>
                  {r.assignment_type === "tutor" ? (
                    <Link
                      to="/tutor"
                      search={{ id: r.id }}
                      className="flex min-w-0 flex-1 items-start gap-4"
                    >
                      <div className="mt-0.5 rounded-lg bg-primary/10 p-2 text-primary">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-accent-foreground">
                            {meta.label}
                          </span>
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {new Date(r.created_at).toLocaleDateString(undefined, {
                              year: "numeric", month: "short", day: "numeric",
                            })}
                          </span>
                        </div>
                        <h3 className="mt-1 truncate font-display text-base font-semibold group-hover:text-primary">
                          {r.title}
                        </h3>
                        {r.prompt && (
                          <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{r.prompt}</p>
                        )}
                        <p className="mt-1 text-xs font-medium text-primary">Pick up where you left off →</p>
                      </div>
                    </Link>
                  ) : (
                    <Link
                      to="/history/$id"
                      params={{ id: r.id }}
                      className="flex min-w-0 flex-1 items-start gap-4"
                    >
                      <div className="mt-0.5 rounded-lg bg-primary/10 p-2 text-primary">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-accent-foreground">
                            {meta.label}
                          </span>
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {new Date(r.created_at).toLocaleDateString(undefined, {
                              year: "numeric", month: "short", day: "numeric",
                            })}
                          </span>
                        </div>
                        <h3 className="mt-1 truncate font-display text-base font-semibold group-hover:text-primary">
                          {r.title}
                        </h3>
                        {r.prompt && (
                          <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{r.prompt}</p>
                        )}
                      </div>
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={() => onDelete(r.id)}
                    disabled={deletingId === r.id}
                    className="rounded-md p-2 text-muted-foreground transition hover:bg-muted hover:text-destructive disabled:opacity-50"
                    aria-label="Delete outline"
                  >
                    {deletingId === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </button>
                </div>
              );
            })
          )}
        </div>
      </main>
    </div>
  );
}
